// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import parse from 'html-react-parser';

import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import {
  finishLine,
  inProgressPaperSubmission,
} from '../../config/constant';
import { setIsAllAnswered } from '../../slices/examSlice/ongoingExam';
import { saveAnswerSheet } from '../../slices/examSlice/submitAnswerSheet';
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../slices/store';
import { CountDownTimer } from './CountDownTimer';
import McqSubmissionDialog from './McqSubmissionDialog';

const MemoizedCountDownTimer = memo(CountDownTimer);

export const McqExamPaper = ({setConflict = () => false}: {setConflict?: (value: boolean) => void }) => {
  const examPaperState = useAppSelector((state: RootState) => state.examPaper);
  const selectedExamState = useAppSelector(
    (state: RootState) => state.selectedExam
  );
  const appState = useAppSelector((state: RootState) => state.app);
  const examPaper = examPaperState.response;
  const snapshot = useRef(
    JSON.parse(JSON.stringify(examPaper.emptyAnswerSheet))
  );
  const [answers, setAnswers] = useState([...snapshot.current.answers]);
  var questionNumber = 1;
  var isHtml = RegExp.prototype.test.bind(
    /<\/?(?:b|i|u|strong|em|p|br|div|span|h[1-6]|blockquote|pre|code|a|[ou]l|li|img|video|iframe|s(?:ub|up)|mark|del|ins|hr)(?:\s+[^>]*)?>/i
  );
  var isHtmlCode = RegExp.prototype.test.bind(/<code>(([^>]+))/i);
  const dispatch = useAppDispatch();

  useEffect(() => {
    checkAllQuestionsAnswered();
  }, []);

  const handleChange = (event: any, question: any) => {
    snapshot.current.answers.find(
      (item: { answerBlockId: any }) =>
        item.answerBlockId === question.answerBlockId
    ).answers = [event.target.value];
    setAnswers([...snapshot.current.answers]);
    saveAnswers();
    checkAllQuestionsAnswered();
  };

  const handleChangeMultiple = (event: any, question: any) => {
    const result = snapshot.current.answers.find(
      (item: { answerBlockId: any }) =>
        item.answerBlockId === question.answerBlockId
    );
    var answerIndex = result.answers.indexOf(event.target.value);
    if (answerIndex !== -1) {
      result.answers.splice(result.answers.indexOf(event.target.value), 1);
    } else {
      result.answers.push(event.target.value);
    }
    setAnswers([...snapshot.current.answers]);
    saveAnswers();
    checkAllQuestionsAnswered();
  };

  const checkAllQuestionsAnswered = () => {
    var flag = true;
    snapshot.current.answers.forEach((element: { answers: string | any[] }) => {
      if (element.answers.length == 0) {
        flag = false;
      }
    });
    dispatch(setIsAllAnswered(flag));
  };

  const saveAnswers = () => {
    let currentSnapshot = snapshot.current;
    let selectedExam = selectedExamState.response[0];
    currentSnapshot.paperName = `${
      selectedExam?.examVisibleType
        ? selectedExam?.examVisibleType
        : selectedExam?.examType
    }`;
    let payload = {
      examId: selectedExam?.id,
      answers: currentSnapshot,
      isPaperInProgress: true,
    };
    if (
      !appState.backgroundProcess &&
      appState.response.isExamResumptionEnabled
    ) {
      setTimeout(() => {
        dispatch(saveAnswerSheet(payload));
      }, inProgressPaperSubmission.requestDelay);
    }
  };

  return (
    <Box translate="no">
      <MemoizedCountDownTimer time={examPaper?.timeForPaper} />
      {examPaper?.generatedQuestionSet.map((category: any, index: number) => (
        <Box key={index}>
          {category.questionSet.map((question: any, indexQ: number) => (
            <List
              key={indexQ}
              sx={{
                width: "100%",
              }}
            >
              <Divider />
              <ListItem alignItems="flex-start">
                <Box
                  sx={{
                    backgroundColor: "primary.main",
                    padding: 1,
                    marginRight: 2,
                    color: "secondary.main",
                    fontWeight: "bold",
                    marginTop: 2,
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                  translate="no"
                >
                  Q{questionNumber++}:
                </Box>
                <Box mt={2}>
                  {isHtml(question.content.toString()) ||
                  isHtmlCode(question.content.toString()) ? (
                    <Typography component={"h1"} mt={-2}>
                      {parse(question.content.toString())}
                    </Typography>
                  ) : (
                    <Typography component={"span"}>
                      {question.content.toString()}
                    </Typography>
                  )}
                  {question.isMultipleSelection ? (
                    <Typography
                      variant="subtitle1"
                      sx={{ fontStyle: "italic" }}
                      component="p"
                      translate="no"
                    >
                      (Select all that apply)
                    </Typography>
                  ) : null}
                </Box>
              </ListItem>
              <Box
                sx={{
                  textAlign: "center",
                  verticalAlign: "middle",
                }}
              >
                <img width={"70%"} src={question.otherContent} />
              </Box>
              {/* // Answer block */}
              <Box translate="no">
                <FormControl>
                  {!question.isMultipleSelection ? (
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue="false"
                      name="radio-buttons-group"
                      sx={{ ml: 10 }}
                      value={
                        answers.find(
                          (item) =>
                            item.answerBlockId === question.answerBlockId
                        )?.answers[0] || ""
                      }
                      onChange={(event) => handleChange(event, question)}
                    >
                      {question.answerList.map(
                        (answer: any, indexA: number) => (
                          <FormControlLabel
                            sx={{ alignItems: "flex-start" }}
                            key={indexA}
                            value={answer.id}
                            control={<Radio />}
                            label={
                              isHtml(answer.answer.toString()) ||
                              isHtmlCode(answer.answer.toString()) ? (
                                <Typography component={"span"} mt={1}>
                                  {parse(answer.answer.toString())}
                                </Typography>
                              ) : (
                                <Typography component={"span"} mt={1}>
                                  {answer.answer.toString()}
                                </Typography>
                              )
                            }
                          />
                        )
                      )}
                    </RadioGroup>
                  ) : (
                    <FormGroup sx={{ ml: 10 }}>
                      {question.answerList.map(
                        (answer: any, indexA: number) => (
                          <FormControlLabel
                            sx={{ alignItems: "flex-start" }}
                            key={indexA}
                            value={answer.id}
                            control={
                              <Checkbox
                                checked={
                                  answers
                                    .find(
                                      (item: { answerBlockId: any }) =>
                                        item.answerBlockId ===
                                        question.answerBlockId
                                    )
                                    ?.answers.find(
                                      (item: string) =>
                                        item === answer.id.toString()
                                    )
                                    ? true
                                    : false
                                }
                                onChange={(event) =>
                                  handleChangeMultiple(event, question)
                                }
                              />
                            }
                            label={
                              isHtml(answer.answer.toString()) ||
                              isHtmlCode(answer.answer.toString()) ? (
                                <Typography component={"span"} mt={1}>
                                  {parse(answer.answer.toString())}
                                </Typography>
                              ) : (
                                <Typography component={"span"} mt={1}>
                                  {answer.answer.toString()}
                                </Typography>
                              )
                            }
                          />
                        )
                      )}
                    </FormGroup>
                  )}
                </FormControl>
              </Box>
            </List>
          ))}
        </Box>
      ))}
      <Box py={10} translate="no">
        <Divider sx={{ marginBottom: 5 }} />
        <Typography sx={{ fontWeight: "bold" }} variant="body1" translate="no">
          Finish Line
        </Typography>
        <Typography variant="body2">{finishLine.mcq}</Typography>
        <McqSubmissionDialog setConflict={setConflict} snapshot={snapshot.current} />
        <Typography sx={{ textDecoration: "underline" }} variant="body1" translate="no">
          NOTICE
        </Typography>
        <Typography
          sx={{ fontStyle: "italic", color: "#3E5060" }}
          variant="body2"
        >
          {finishLine.notice}
        </Typography>
      </Box>
    </Box>
  );
};
